import { useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Flex, IconError, Spacer } from '@zillow/constellation';
import { Button, FieldSet, Text, TextButton } from '@zillow/constellation-10';

import {
  DatarouterError,
  useRentalManagerUserEmailVerificationWebApiHandler_sendVerificationCode,
  useRentalManagerUserEmailVerificationWebApiHandler_validateVerificationCode,
} from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { useSelector } from '../../../../../selectors/useSelector';

import PostingActions from '../../../../../actions/PostingActions';

import numberUtils from '../../../../../utils/numberUtils';

import StringConstants from '../../../../../constants/StringConstants';
import { MODES, SOURCES, VERIFICATION_STEPS } from '../../constants';

import VerificationCodeValidator from '../../../../VerificationCodeValidator';

import VerificationBodyTemplate from '../VerificationBodyTemplate';
import MaxAttemptsError from '../MaxAttemptsError';

import { StyledLegend } from '../../StyledComponents';

import type { IAPIErrorResponse } from '../../../../../interfaces';
import type { MODES_VALUES, SOURCES_VALUES } from '../../constants';
import type { ErrorRMWProps } from '../../../../../model/ErrorRMW';

const CONFIG = {
  phone: {
    value: (val: string) => numberUtils.formatPhoneNumberInput(val),
    editButtonText: 'Edit number',
  },
  email: {
    value: (val: string) => val,
    editButtonText: 'Edit email',
  },
};

const VERIFICATION_CODE_LENGTH = 5;

export type Props = {
  contentValue: string;
  currentStepIndex: number;
  incrementStep: (step?: string) => void;
  isContentEditable?: boolean;
  verificationType: string;
  onVerificationConfirmation?: (phoneNumber: string) => Promise<void>;
  verificationMode?: MODES_VALUES;
  verificationSource?: SOURCES_VALUES;
};

const VerificationCodeSent = ({
  contentValue,
  currentStepIndex,
  incrementStep,
  isContentEditable = true,
  onVerificationConfirmation,
  verificationType,
  verificationMode = MODES.PHONE,
  verificationSource = SOURCES.LISTING_CONTACT,
}: Props) => {
  const dispatch = useDispatch();

  const { listingAlias } = useSelector(
    (state) => ({ listingAlias: state.properties.currentProperty.listingAlias }),
    shallowEqual,
  );

  const [error, setError] = useState<Partial<ErrorRMWProps> | undefined>({});
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { mutate: sendVerificationCode } = useRentalManagerUserEmailVerificationWebApiHandler_sendVerificationCode();
  const { mutate: validateVerificationCode } =
    useRentalManagerUserEmailVerificationWebApiHandler_validateVerificationCode();

  const isButtonDisabled = error?.code === StringConstants.SS_ERRORS.attempt_limit_reached;

  const handleConfirmPhoneVerification = () => {
    const contactObj = {
      phone: contentValue,
      contactType: verificationType,
      verificationCode,
      verificationSource,
    };

    dispatch(PostingActions.property.confirmVerification(contactObj))
      // @ts-expect-error
      .then((result: IAPIErrorResponse) => {
        if (result.success) {
          if (onVerificationConfirmation) {
            onVerificationConfirmation(contentValue)
              .then(() => incrementStep())
              .catch(({ code, message }: ErrorRMWProps) =>
                setError({
                  code,
                  message: message || 'Something went wrong with verification confirmation. Try again.',
                }),
              );
          } else {
            incrementStep();
          }
        } else {
          if (result && result.error && result.error.message) {
            setError({
              code: result?.error?.code,
              message: result.error.message,
            });
          }

          setIsLoading(false);
        }
      });
  };

  const handleConfirmEmailVerification = () => {
    return validateVerificationCode(
      {
        getParams: {
          listingAlias,
          email: contentValue,
          verificationCode,
        },
      },
      {
        onSuccess: () => {
          onVerificationConfirmation?.(contentValue).then(() => incrementStep());
        },
        onError: (err) => {
          const errorCode = DatarouterError.getDisplayCode(err);
          const errorMessage = DatarouterError.getDisplayMessage(err);

          if (errorMessage) {
            setError({ code: errorCode, message: errorMessage });
            logError({
              error: err,
              context: 'requestVerificationCode',
            });
          }

          setIsLoading(false);
        },
      },
    );
  };

  const confirmVerification = () => {
    if (verificationCode.length < VERIFICATION_CODE_LENGTH) {
      setError({ message: 'Please enter a 5 digit verification code.' });
      return;
    }

    setIsLoading(true);

    switch (verificationMode) {
      case MODES.PHONE:
        handleConfirmPhoneVerification();

        break;
      case MODES.EMAIL:
        handleConfirmEmailVerification();

        break;
      default:
        return;
    }
  };

  const handleGetNewCodeClick = () => {
    switch (verificationMode) {
      case MODES.PHONE:
        incrementStep(VERIFICATION_STEPS.GET_VERIFICATION_CODE);

        break;
      case MODES.EMAIL:
        setIsLoading(true);

        sendVerificationCode(
          { getParams: { listingAlias, email: contentValue } },
          {
            onError: (err) => {
              const errorMessage = DatarouterError.getDisplayMessage(err);

              if (errorMessage) {
                setError({ message: errorMessage });
                logError({
                  error: err,
                  context: 'requestVerificationCode',
                });
              }
            },
            onSettled: () => setIsLoading(false),
          },
        );

        break;
      default:
        return;
    }
  };

  const renderErrorMessage = () => {
    const message = error?.message || 'There was an error verifying your code.';

    if (isButtonDisabled) {
      return <MaxAttemptsError verificationMode={verificationMode} />;
    }

    return (
      <Spacer marginTop="sm">
        <Flex display="flex" alignItems="center" justifyContent="center">
          <IconError fontColor="red600" size="sm" marginRight="xs" title="Error" />
          <Text css={{ color: 'text.feedback.critical.strong', lineHeight: '18px', textAlign: 'center' }} asChild>
            <p>{`${message}`}</p>
          </Text>
        </Flex>
      </Spacer>
    );
  };

  return (
    <VerificationBodyTemplate
      heading={`Step ${currentStepIndex + 1} - Verification code sent`}
      subheading={
        <Text className="fs-mask">
          We sent the 5 digit code to
          {` ${CONFIG[verificationMode].value(contentValue)}. `}
          {isContentEditable && (
            <TextButton
              tone="brand"
              onClick={() => incrementStep(VERIFICATION_STEPS.ADD_CONTENT)}
              css={{
                fontSize: 'body-sm',
                fontWeight: 400,
                textDecoration: 'underline',
                xl: {
                  fontSize: 'body',
                },
              }}
            >
              {CONFIG[verificationMode].editButtonText}
            </TextButton>
          )}
        </Text>
      }
    >
      <Flex display="flex" alignItems="center" flexDirection="column">
        <FieldSet
          legend={<StyledLegend>Enter the verification code below.</StyledLegend>}
          css={{
            margin: '{spacing.400} auto 0',
          }}
        >
          <VerificationCodeValidator
            codeLength={VERIFICATION_CODE_LENGTH}
            onComplete={setVerificationCode}
            clearErrors={setError}
            onEnterKeyPress={confirmVerification}
          />

          <Spacer marginTop="sm" marginBottom="md">
            <Text textStyle="body-xs">
              Didn&#39;t receive the code?&nbsp;
              <TextButton tone="brand" disabled={isLoading} onClick={handleGetNewCodeClick}>
                <TextButton.Label css={{ textDecoration: 'underline', textStyle: 'body-xs-bold' }}>
                  Get new code
                </TextButton.Label>
              </TextButton>
            </Text>
          </Spacer>

          <Button
            tone="brand"
            emphasis="filled"
            disabled={isButtonDisabled}
            loading={isLoading}
            onClick={confirmVerification}
            css={{
              minWidth: '200px',
              display: 'block',
              margin: '0 auto',
            }}
          >
            Check code
          </Button>
        </FieldSet>
      </Flex>

      {Boolean(error?.message) && (
        <Spacer marginY="xs" aria-live="assertive">
          {renderErrorMessage()}
        </Spacer>
      )}
    </VerificationBodyTemplate>
  );
};

export default VerificationCodeSent;
